import { useEffect, useRef, useState } from 'react'

import { posicionParaSello } from '@/modules/campo/estampar'
import { buscarDireccion, esPuntoFino, puntoDeFiar } from '@/lib/geocodificacion/google'
import { cargarGoogleMaps, hayLlaveDeGoogle, ESTILO_MAPA } from '@/lib/mapas/google'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'

import estilos from './SelectorUbicacion.module.css'

/**
 * PONER LA UBICACIÓN DE LA OBRA AL CREAR, NO DESPUÉS.
 *
 * Hasta hoy el técnico escribía la dirección y la orden nacía sin punto: alguien
 * en la oficina tenía que geocodificarla —y una finca o un sitio con nombre no
 * los encuentra Google—, o entrar al mapa de calor a ubicarla a mano. Pero el
 * técnico está PARADO en la obra: su teléfono ya sabe dónde es mejor que nadie.
 *
 * Por eso lo primero, y en grande, es «Usar mi ubicación»: un toque y el punto
 * queda tomado del GPS, marcado como puesto a mano, que es la mejor fuente que
 * hay. El mapa está debajo para el que no está en el sitio —cotiza una obra que
 * le señalan— o para afinar el alfiler si el GPS quedó corto.
 *
 * No guarda nada: el punto todavía no tiene orden a la que colgarse. Lo entrega
 * por `onPunto` y quien crea la orden lo guarda con ella.
 */

/** Cartago, plaza de Bolívar: el centro razonable cuando no hay punto aún. */
const CARTAGO = { lat: 4.746, lng: -75.9115 }

type Punto = { lat: number; lng: number }

type Props = {
  punto: Punto | null
  onPunto: (p: Punto | null) => void
  /** Para el botón «Buscar por la dirección», que parte de lo ya escrito. */
  direccion?: string
  ciudad?: string | null
  departamento?: string | null
}

export function SelectorUbicacion({ punto, onPunto, direccion, ciudad, departamento }: Props) {
  const [mapaAbierto, setMapaAbierto] = useState(false)
  const [tomandoGps, setTomandoGps] = useState(false)
  const [buscando, setBuscando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [listo, setListo] = useState(false)

  const contenedor = useRef<HTMLDivElement | null>(null)
  const mapa = useRef<google.maps.Map | null>(null)
  const alfiler = useRef<google.maps.Marker | null>(null)

  // Sin llave de Google no hay mapa, pero el GPS sigue sirviendo: el técnico en
  // la obra puede tomar su punto igual. Solo se esconde el mapa.
  const hayMapa = hayLlaveDeGoogle()

  // Monta el mapa al abrirlo. El alfiler se arrastra o se pone con un toque; el
  // punto es siempre el que quede debajo.
  useEffect(() => {
    if (!mapaAbierto || !hayMapa || !contenedor.current) return

    let vivo = true
    const inicio = punto ?? CARTAGO

    void cargarGoogleMaps().then(({ maps }) => {
      if (!vivo || !contenedor.current) return

      const m = new maps.Map(contenedor.current, {
        styles: ESTILO_MAPA,
        center: inicio,
        zoom: punto ? 17 : 14,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      })

      const pin = new google.maps.Marker({
        map: m,
        position: inicio,
        draggable: true,
        title: 'Arrástralo al sitio exacto',
      })

      const fijar = (pos: google.maps.LatLng | null | undefined) => {
        if (!pos) return
        onPunto({ lat: pos.lat(), lng: pos.lng() })
      }

      pin.addListener('dragend', () => fijar(pin.getPosition()))
      m.addListener('click', (e: google.maps.MapMouseEvent) => {
        pin.setPosition(e.latLng)
        fijar(e.latLng)
      })

      mapa.current = m
      alfiler.current = pin
      setListo(true)
    })

    return () => {
      vivo = false
      mapa.current = null
      alfiler.current = null
      setListo(false)
    }
    // Solo se remonta al abrir/cerrar; dentro el punto lo mueve el usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapaAbierto])

  /** El punto del GPS: el técnico está en la obra y el teléfono lo sabe. */
  async function usarMiUbicacion() {
    setTomandoGps(true)
    setAviso(null)
    try {
      const pos = await posicionParaSello()
      if (!pos) {
        setAviso('No se pudo leer el GPS. Revisa el permiso de ubicación, o ponla en el mapa.')
        return
      }
      const p = { lat: pos.coords.latitude, lng: pos.coords.longitude }
      onPunto(p)
      // Si el mapa está abierto, lleva el alfiler a donde estoy.
      alfiler.current?.setPosition(p)
      mapa.current?.panTo(p)
      mapa.current?.setZoom(18)
    } finally {
      setTomandoGps(false)
    }
  }

  /** Buscar por la dirección escrita, como punto de partida para afinar. */
  async function buscarPorLaDireccion() {
    if (!direccion || direccion.trim().length < 4) {
      setAviso('Escribe primero la dirección de arriba.')
      return
    }
    setBuscando(true)
    setAviso(null)
    // El municipio del cliente; sin él, Cartago, donde trabaja MEGA. Sin
    // ninguno Google devolvía «Colombia» y el alfiler caía en el centro del
    // país (OS-2026-0104).
    const donde = { direccion, ciudad: ciudad || 'Cartago', departamento: departamento ?? '' }
    try {
      const encontrado = await buscarDireccion(donde)
      if (!encontrado) {
        setAviso(`No se encontró esa dirección en ${donde.ciudad}. Ubícala a mano en el mapa.`)
        return
      }
      // Lo que Google devuelve cuando no sabe —el centro del municipio o del
      // país, o una dirección parecida— no se pone como punto.
      if (!esPuntoFino(encontrado.precision)) {
        setAviso(
          `Google no encontró la dirección en ${donde.ciudad}: solo devolvió «${encontrado.texto}». Ubícala a mano en el mapa o usa tu ubicación.`,
        )
        return
      }
      const veredicto = puntoDeFiar(encontrado, donde)
      if (!veredicto.sirve) {
        setAviso(`${veredicto.motivo} Ubícala a mano en el mapa o usa tu ubicación.`)
        return
      }
      const p = { lat: encontrado.latitud, lng: encontrado.longitud }
      onPunto(p)
      alfiler.current?.setPosition(p)
      mapa.current?.panTo(p)
      mapa.current?.setZoom(17)
    } catch {
      setAviso('No se pudo buscar la dirección. Ubícala a mano en el mapa.')
    } finally {
      setBuscando(false)
    }
  }

  return (
    <div className={estilos.zona}>
      <span className={estilos.rotulo}>Ubicación de la obra</span>

      {/* El estado, primero: si ya hay punto se ve, con las coordenadas y de
          dónde salió; el mapa se abre para ajustarlo, no para enterarse. */}
      {punto ? (
        <div className={estilos.tomada}>
          <span className={estilos.check} aria-hidden="true">
            ✓
          </span>
          <div className={estilos.tomadaTexto}>
            <b>Ubicación tomada</b>
            <span className={estilos.coords}>
              {punto.lat.toFixed(6)}, {punto.lng.toFixed(6)}
            </span>
          </div>
          <button type="button" className={estilos.quitar} onClick={() => onPunto(null)}>
            Quitar
          </button>
        </div>
      ) : (
        <p className={estilos.ayuda}>
          Estás en la obra: toma el punto del GPS. Así la orden sale en el mapa desde ya, sin
          esperar a que la oficina la ubique.
        </p>
      )}

      <div className={estilos.acciones}>
        <Boton variante="principal" onClick={() => void usarMiUbicacion()} trabajando={tomandoGps}>
          📍 Usar mi ubicación
        </Boton>
        {hayMapa && (
          <Boton variante="secundario" onClick={() => setMapaAbierto((v) => !v)}>
            {mapaAbierto ? 'Cerrar el mapa' : punto ? 'Ajustar en el mapa' : 'Ponerla en el mapa'}
          </Boton>
        )}
      </div>

      {aviso && (
        <div className={estilos.avisoCaja}>
          <Aviso tipo="alerta">{aviso}</Aviso>
        </div>
      )}

      {mapaAbierto && hayMapa && (
        <div className={estilos.editor}>
          <p className={estilos.ayudaMapa}>
            Arrastra el alfiler —o toca el mapa— hasta el sitio exacto.
          </p>
          <div ref={contenedor} className={estilos.mapa} />
          <div className={estilos.pieMapa}>
            <Boton
              variante="plano"
              onClick={() => void buscarPorLaDireccion()}
              trabajando={buscando}
              disabled={!listo}
            >
              Buscar por la dirección
            </Boton>
          </div>
        </div>
      )}
    </div>
  )
}
